import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StatusCardConfig } from '@shared/components/widgets/status-card/status-card-config';
import { DashboardService, GridLossService } from '@app/api/dmc';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ODataType } from '@app/core/services/odata/types/odata-type';
import { PreferencesService } from '@app/core/services/preferences.service';
import { GridLossGraphSeries } from '@app/api/dmc/model/gridLossGraphSeries';
import { StatusCardComponent } from '@shared/components/widgets/status-card/status-card.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EChartComponent,
  EChartAxisPointerValue,
  EChartDataZoomRange,
  EChartLegendSelection,
  EChartValueRange,
} from '@shared/components/echart/echart.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { DropdownComponent } from '@shared/components/input/dropdown/dropdown.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { Calendar, Download, Maximize2, X, LucideAngularModule } from 'lucide-angular';
import { DatePipe } from '@angular/common';
import { debounceTime, distinctUntilChanged, map, Subscription } from 'rxjs';
import {
  buildEstimatedTimeSeriesChartData,
  EstimatedTimeSeriesChartData,
  EstimatedTimeSeriesChartSeries,
} from './estimated-timeseries-chart';

@Component({
  selector: 'app-dashboard-tidsserier',
  imports: [
    StatusCardComponent,
    TranslatePipe,
    EChartComponent,
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDateDirective,
    DropdownComponent,
    LucideAngularModule,
  ],
  templateUrl: './dashboard-tidsserier.component.html',
  providers: [DatePipe],
})
export class DashboardTidsseriComponent implements OnInit, OnDestroy {

  private readonly subs = new Subscription();

  public missingTimeSeries: StatusCardConfig = {
    label: 'dashboard.timeseries.missing.title',
    route: ['/dashboard', 'timeseries', 'missing'],
    limit: 550,
    serviceCallback: () => this.dashboardService.dashboardQueryMissingMeteredDataCountPost(),
  };

  public unbalancedQuantity: StatusCardConfig = {
    label: 'dashboard.query.unbalancedquantity.title',
    route: ['/dashboard', 'unbalancedquantity'],
    serviceCallback: () => this.dashboardService.dashboardQueryUnbalancedQuantityPost({ page: 0, pageSize: 0 }),
  };

  public lockedTimeSeries: StatusCardConfig = {
    label: 'dashboard.lockedtimeseries.title',
    route: ['/dashboard', 'timeseries', 'locked'],
    serviceCallback: () => this.dashboardService.dashboardQueryMeteredDataLockPost({
      page: 0,
      pageSize: 0,
      meteringPointIdentification: '',
    }),
  };

  public timeSeriesNotCreated: StatusCardConfig = {
    label: 'dashboard.query.metereddatanotcreated.title',
    route: ['/dashboard', 'query', 'metereddatanotcreated'],
    serviceCallback: () => this.dashboardService.dashboardGetAllMeteredDataNotCreatedGet(),
  };

  public usedVerificationMeterReadings: StatusCardConfig = {
    label: 'dashboard.query.queryusedverificationmeterreadings',
    route: ['/dashboard', 'timeseries', 'usedverificationmeterreadings'],
    serviceCallback: () => this.oDataService.dmc.getCount(ODataType.usedVerificationMeterReadings).pipe(map(count => ({ totalHitCount: count }))),
  };

  public deviatingMeters: StatusCardConfig = {
    label: 'dashboard.query.querydeviatingmeters',
    route: ['/dashboard', 'timeseries', 'deviatingmeters'],
    serviceCallback: () => this.oDataService.dmc.getCount(ODataType.deviatingMeters).pipe(map(count => ({ totalHitCount: count }))),
  };

  /** ECharts options for the generated timeseries bar chart */
  public generatedChartOptions: any = null;
  public generatedChartLoading = true;
  public generatedChartError = false;
  public generatedChartEmpty = false;

  /** ECharts options for the estimated timeseries bar chart */
  public estimatedChartOptions: any = null;
  public estimatedChartLoading = true;
  public estimatedChartError = false;
  public estimatedChartEmpty = false;
  public estimatedLegendItems: EstimatedTimeSeriesChartSeries[] = [];

  /** Stable skeleton bar heights for the loading state */
  public readonly skeletonBars: number[] = Array.from({ length: 20 }, (_, i) =>
    30 + ((i * 37 + 13) % 55)
  );

  /** Grid Loss */
  public gridLossChartOptions: any = null;
  public gridLossLoading = true;
  public gridLossError = false;
  public gridLossEmpty = false;
  public gridLossFullscreen = false;
  private gridLossSeriesData: GridLossGraphSeries[] = [];

  /** Computed Grid Loss */
  public computedChartOptions: any = null;
  public computedLoading = true;
  public computedError = false;
  public computedEmpty = false;
  public computedFullscreen = false;
  private computedSeriesData: GridLossGraphSeries[] = [];
  public synchronizeGridLossCharts = false;

  protected readonly Calendar = Calendar;
  protected readonly Download = Download;
  protected readonly Maximize2 = Maximize2;
  protected readonly X = X;

  @ViewChild('gridLossChart') public gridLossChart?: EChartComponent;
  @ViewChild('gridLossChartFullscreen') public gridLossChartFullscreen?: EChartComponent;
  @ViewChild('computedChart') public computedChart?: EChartComponent;
  @ViewChild('computedChartFullscreen') public computedChartFullscreen?: EChartComponent;

  private readonly gridLossSeriesColors: Record<string, string> = {
    netLoss: '#3b82f6',
    systemCorrection: '#ef4444',
    e17: '#10b981',
    e17Flex: '#f59e0b',
    e17Hour: '#8b5cf6',
    e18: '#ec4899',
    e20: '#06b6d4',
  };

  private readonly gridLossSeriesLabelKeys: Record<string, string> = {
    netLoss: 'gridloss.series.netLoss',
    systemCorrection: 'gridloss.series.systemCorrection',
    e17: 'gridloss.series.e17',
    e17Flex: 'gridloss.series.e17Flex',
    e17Hour: 'gridloss.series.e17Hour',
    e18: 'gridloss.series.e18',
    e20: 'gridloss.series.e20',
  };

  public readonly gridLossDaysActions: MenuAction[] = [
    { id: '7',  title: 'gridloss.period.week' },
    { id: '14', title: 'gridloss.period.twoWeeks' },
    { id: '21', title: 'gridloss.period.threeWeeks' },
    { id: '30', title: 'gridloss.period.month' },
  ];

  public selectedDays = '14';

  public readonly gridLossForm = new FormGroup({
    startDate: new FormControl<string>((() => {
      const d = new Date();
      d.setDate(d.getDate() - 14);
      return d.toISOString().slice(0, 10);
    })()),
  });

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly translateService: TranslateService,
    private readonly gridLossService: GridLossService,
    private readonly datePipe: DatePipe,
    public readonly preferencesService: PreferencesService,
    private readonly oDataService: ODataService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.loadGeneratedTimeSeries();
    this.loadEstimatedTimeSeries();
    this.loadGridLoss();
    if (this.preferencesService.dashboardComputedGridloss()) {
      this.loadComputedGridLoss();
    }

    this.subs.add(
      this.gridLossForm.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
        this.loadGridLoss();
        this.loadComputedGridLoss();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadGeneratedTimeSeries(): void {
    this.subs.add(
      this.dashboardService.dashboardQueryGeneratedTimeSeriesGet(30).subscribe({
        next: (result) => {
          const sorted = [...(result ?? [])].sort(
            (a, b) => new Date(a.date ?? '').getTime() - new Date(b.date ?? '').getTime(),
          );
          this.generatedChartLoading = false;
          if (!sorted.length) { this.generatedChartEmpty = true; return; }
          const dates  = sorted.map(p => this.fmtDate(p.date ?? ''));
          const counts = sorted.map(p => Number(p.count ?? 0));
          this.generatedChartOptions = this.buildBarChartOptions(dates, counts);
        },
        error: () => {
          this.generatedChartLoading = false;
          this.generatedChartError = true;
        },
      })
    );
  }

  private loadEstimatedTimeSeries(): void {
    this.subs.add(
      this.dashboardService.dashboardQueryEstimatedTimeSeriesGet(30).subscribe({
        next: (result) => {
          const chartData = buildEstimatedTimeSeriesChartData(
            result ?? [],
            date => this.fmtDate(date),
            method => this.getEstimationMethodLabel(method),
          );
          this.estimatedChartLoading = false;
          if (!chartData.series.length) { this.estimatedChartEmpty = true; return; }
          this.estimatedLegendItems = chartData.series;
          this.estimatedChartOptions = this.buildEstimatedBarChartOptions(chartData);
        },
        error: () => {
          this.estimatedChartLoading = false;
          this.estimatedChartError = true;
        },
      })
    );
  }

  private fmtDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  private getEstimationMethodLabel(method: string): string {
    const key = `enum.estimationmethod.${method.toLowerCase()}`;
    const label = this.translateService.instant(key);
    return label === key ? method : label;
  }

  public onDaysChange(value: string | string[]): void {
    this.selectedDays = value as string;
    this.loadGridLoss();
    this.loadComputedGridLoss();
  }

  public setSynchronizeGridLossCharts(enabled: boolean): void {
    this.synchronizeGridLossCharts = enabled;
    if (!enabled) {
      this.setGridLossYAxisRange();
      return;
    }

    const range = this.gridLossChart?.getDataZoomRange();
    if (range) {
      this.updateComputedChartTimeWindow(range);
    }

    const legendSelection = this.gridLossChart?.getLegendSelection();
    if (legendSelection) {
      this.updateComputedChartLegendSelection(legendSelection);
    }

    this.synchronizeGridLossYAxis(range);
  }

  public onGridLossDataZoomChange(range: EChartDataZoomRange, source: 'gridLoss' | 'computed'): void {
    if (!this.synchronizeGridLossCharts) {
      return;
    }

    if (source === 'gridLoss') {
      this.updateGridLossChartTimeWindow(range);
      this.updateComputedChartTimeWindow(range);
    } else {
      this.updateComputedChartTimeWindow(range);
      this.updateGridLossChartTimeWindow(range);
    }

    this.synchronizeGridLossYAxis(range);
  }

  public onGridLossLegendSelectionChange(
    selection: EChartLegendSelection,
    source: 'gridLoss' | 'computed'
  ): void {
    if (!this.synchronizeGridLossCharts) {
      return;
    }

    if (source === 'gridLoss') {
      this.updateGridLossChartLegendSelection(selection);
      this.updateComputedChartLegendSelection(selection);
    } else {
      this.updateComputedChartLegendSelection(selection);
      this.updateGridLossChartLegendSelection(selection);
    }

    this.synchronizeGridLossYAxis(this.gridLossChart?.getDataZoomRange());
  }

  public onGridLossAxisPointerChange(
    value: EChartAxisPointerValue,
    source: 'gridLoss' | 'computed'
  ): void {
    if (!this.synchronizeGridLossCharts) {
      return;
    }

    if (source === 'gridLoss') {
      this.updateGridLossChartAxisPointer(value);
      this.updateComputedChartAxisPointer(value);
      return;
    }

    this.updateComputedChartAxisPointer(value);
    this.updateGridLossChartAxisPointer(value);
  }

  public resetGridLossChart(source: 'gridLoss' | 'computed'): void {
    let chart: EChartComponent | undefined;
    if (source === 'gridLoss') {
      chart = this.gridLossFullscreen ? this.gridLossChartFullscreen : this.gridLossChart;
    } else {
      chart = this.computedFullscreen ? this.computedChartFullscreen : this.computedChart;
    }

    chart?.restore();

    if (this.synchronizeGridLossCharts) {
      this.onGridLossDataZoomChange({ start: 0, end: 100 }, source);
      this.onGridLossLegendSelectionChange(chart?.getLegendSelection() ?? {}, source);
    }
  }

  public loadGridLoss(): void {
    const startDate = this.gridLossForm.value.startDate;
    if (!startDate) return;

    this.gridLossLoading = true;
    this.gridLossError = false;
    this.gridLossEmpty = false;
    this.gridLossSeriesData = [];
    this.gridLossChartOptions = null;

    this.subs.add(
      this.gridLossService.gridLossGet(startDate, Number(this.selectedDays)).subscribe({
        next: (result) => {
          this.gridLossLoading = false;
          const series = result.series ?? [];
          if (!series.length || series.every(s => !s.points?.length)) {
            this.gridLossEmpty = true;
            return;
          }
          this.gridLossSeriesData = series;
          this.rebuildGridLossChartOptions();
        },
        error: () => {
          this.gridLossLoading = false;
          this.gridLossError = true;
        },
      })
    );
  }

  public toggleGridLossFullscreen(): void {
    const range = this.gridLossChart?.getDataZoomRange();
    const legendSelection = this.gridLossChart?.getLegendSelection();
    this.clearGridLossAxisPointers();
    this.gridLossFullscreen = !this.gridLossFullscreen;

    if (!this.gridLossFullscreen || !this.synchronizeGridLossCharts) {
      return;
    }

    this.changeDetectorRef.detectChanges();
    if (range) {
      this.updateGridLossChartTimeWindow(range);
    }
    if (legendSelection) {
      this.updateGridLossChartLegendSelection(legendSelection);
    }
    this.synchronizeGridLossYAxis(range);
  }

  public exportImage(): void {
    const chart = this.gridLossFullscreen ? this.gridLossChartFullscreen : this.gridLossChart;
    chart?.saveAsImage('aggregerede-energitidsserier');
  }

  public exportCsv(): void {
    const series = this.gridLossSeriesData;
    if (!series.length) return;

    // Collect all unique timestamps across all series
    const allTimestamps = new Set<string>();
    for (const s of series) {
      for (const p of s.points ?? []) {
        if (p.timestamp != null) allTimestamps.add(p.timestamp as string);
      }
    }
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a.localeCompare(b));

    // Build header row
    const headers = ['Tidsstempel', ...series.map(s => this.getGridLossSeriesLabel(s.key))];

    // Build value lookup per series
    const pointMaps = series.map(s => {
      const m = new Map<string, number>();
      for (const p of s.points ?? []) {
        if (p.timestamp != null) m.set(p.timestamp as string, Number(p.value ?? 0));
      }
      return m;
    });

    // Build rows
    const rows = sortedTimestamps.map(ts => {
      const values = pointMaps.map(m => m.has(ts) ? String(m.get(ts)) : '');
      return [ts, ...values].join(';');
    });

    const csv = [headers.join(';'), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'aggregerede-energitidsserier.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  public loadComputedGridLoss(): void {
    const startDate = this.gridLossForm.value.startDate;
    if (!startDate) return;

    this.computedLoading = true;
    this.computedError = false;
    this.computedEmpty = false;
    this.computedSeriesData = [];
    this.computedChartOptions = null;

    this.subs.add(
      this.gridLossService.gridLossComputedGet(startDate, Number(this.selectedDays)).subscribe({
        next: (result) => {
          this.computedLoading = false;
          const series = result.series ?? [];
          if (!series.length || series.every(s => !s.points?.length)) {
            this.computedEmpty = true;
            return;
          }
          this.computedSeriesData = series;
          this.rebuildGridLossChartOptions();
        },
        error: () => {
          this.computedLoading = false;
          this.computedError = true;
        },
      })
    );
  }

  public toggleComputedFullscreen(): void {
    const range = this.computedChart?.getDataZoomRange();
    const legendSelection = this.computedChart?.getLegendSelection();
    this.clearGridLossAxisPointers();
    this.computedFullscreen = !this.computedFullscreen;

    if (!this.computedFullscreen || !this.synchronizeGridLossCharts) {
      return;
    }

    this.changeDetectorRef.detectChanges();
    if (range) {
      this.updateComputedChartTimeWindow(range);
    }
    if (legendSelection) {
      this.updateComputedChartLegendSelection(legendSelection);
    }
    this.synchronizeGridLossYAxis(range);
  }

  public exportComputedImage(): void {
    const chart = this.computedFullscreen ? this.computedChartFullscreen : this.computedChart;
    chart?.saveAsImage('beregnede-energitidsserier');
  }

  public exportComputedCsv(): void {
    const series = this.computedSeriesData;
    if (!series.length) return;

    const allTimestamps = new Set<string>();
    for (const s of series) {
      for (const p of s.points ?? []) {
        if (p.timestamp != null) allTimestamps.add(p.timestamp as string);
      }
    }
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a.localeCompare(b));

    const headers = ['Tidsstempel', ...series.map(s => this.getGridLossSeriesLabel(s.key))];

    const pointMaps = series.map(s => {
      const m = new Map<string, number>();
      for (const p of s.points ?? []) {
        if (p.timestamp != null) m.set(p.timestamp as string, Number(p.value ?? 0));
      }
      return m;
    });

    const rows = sortedTimestamps.map(ts => {
      const values = pointMaps.map(m => m.has(ts) ? String(m.get(ts)) : '');
      return [ts, ...values].join(';');
    });

    const csv = [headers.join(';'), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'beregnede-energitidsserier.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private updateGridLossChartTimeWindow(range: EChartDataZoomRange): void {
    this.gridLossChart?.setDataZoomRange(range);
    this.gridLossChartFullscreen?.setDataZoomRange(range);
  }

  private updateComputedChartTimeWindow(range: EChartDataZoomRange): void {
    this.computedChart?.setDataZoomRange(range);
    this.computedChartFullscreen?.setDataZoomRange(range);
  }

  private updateGridLossChartLegendSelection(selection: EChartLegendSelection): void {
    this.gridLossChart?.setLegendSelection(selection);
    this.gridLossChartFullscreen?.setLegendSelection(selection);
  }

  private updateComputedChartLegendSelection(selection: EChartLegendSelection): void {
    this.computedChart?.setLegendSelection(selection);
    this.computedChartFullscreen?.setLegendSelection(selection);
  }

  private updateGridLossChartAxisPointer(value: EChartAxisPointerValue): void {
    if (this.gridLossFullscreen || this.computedFullscreen) {
      this.gridLossChartFullscreen?.setAxisPointer(value);
      return;
    }

    this.gridLossChart?.setAxisPointer(value);
  }

  private updateComputedChartAxisPointer(value: EChartAxisPointerValue): void {
    if (this.gridLossFullscreen || this.computedFullscreen) {
      this.computedChartFullscreen?.setAxisPointer(value);
      return;
    }

    this.computedChart?.setAxisPointer(value);
  }

  private clearGridLossAxisPointers(): void {
    this.gridLossChart?.setAxisPointer(undefined);
    this.gridLossChartFullscreen?.setAxisPointer(undefined);
    this.computedChart?.setAxisPointer(undefined);
    this.computedChartFullscreen?.setAxisPointer(undefined);
  }

  private getGridLossSeriesLabel(key: string): string {
    return this.translateService.instant(this.gridLossSeriesLabelKeys[key] ?? key);
  }

  private rebuildGridLossChartOptions(): void {
    const allTimestamps = new Set<string>();
    for (const s of [...this.gridLossSeriesData, ...this.computedSeriesData]) {
      for (const p of s.points ?? []) {
        if (p.timestamp != null) {
          allTimestamps.add(this.normalizeGridLossTimestamp(p.timestamp as string));
        }
      }
    }
    const labels = Array.from(allTimestamps).sort((a, b) => a.localeCompare(b));
    const sharedYAxisRange = this.synchronizeGridLossCharts
      ? this.getGridLossSeriesValueRange(
          [...this.gridLossSeriesData, ...this.computedSeriesData],
          labels
        )
      : undefined;

    if (this.gridLossSeriesData.length) {
      this.gridLossChartOptions = this.buildGridLossChartOptions(
        this.gridLossSeriesData,
        labels,
        sharedYAxisRange
      );
    }
    if (this.computedSeriesData.length) {
      this.computedChartOptions = this.buildGridLossChartOptions(
        this.computedSeriesData,
        labels,
        sharedYAxisRange
      );
    }
  }

  private buildGridLossChartOptions(
    series: GridLossGraphSeries[],
    labels: string[],
    yAxisRange?: EChartValueRange
  ): any {

    // Day-boundary lines (every midnight)
    const dayMarkLines = labels
      .filter(ts => new Date(ts).getHours() === 0 && new Date(ts).getMinutes() === 0)
      .map(ts => ({
        xAxis: ts,
        lineStyle: { type: 'dashed', color: '#d1d5db', width: 1, opacity: 0.8 },
        label: { show: false },
      }));

    // Weekend shading — one markArea pair per Saturday/Sunday day
    const weekendMarkAreas: [object, object][] = [];
    for (let i = 0; i < labels.length; i++) {
      const d = new Date(labels[i]);
      const dow = d.getDay(); // 0 = Sunday, 6 = Saturday
      if ((dow === 6 || dow === 0) && d.getHours() === 0 && d.getMinutes() === 0) {
        const nextMidnightIdx = labels.findIndex((ts, j) => {
          if (j <= i) return false;
          const nd = new Date(ts);
          return nd.getHours() === 0 && nd.getMinutes() === 0;
        });
        const endLabel = nextMidnightIdx === -1
          ? labels.at(-1)!
          : labels[nextMidnightIdx];
        weekendMarkAreas.push([
          { xAxis: labels[i] },
          { xAxis: endLabel },
        ]);
      }
    }

    const annotationSeries = {
      type: 'line',
      data: [],
      symbol: 'none',
      silent: true,
      tooltip: { show: false },
      lineStyle: { opacity: 0 },
      markLine: {
        silent: true,
        animation: false,
        symbol: ['none', 'none'],
        data: dayMarkLines,
      },
      markArea: {
        silent: true,
        animation: false,
        itemStyle: { color: 'rgba(0,0,0,0.035)' },
        data: weekendMarkAreas,
      },
    };

    const echartSeries = series.map(s => {
      const pointMap = new Map<string, number>();
      for (const p of s.points ?? []) {
        if (p.timestamp != null) {
          pointMap.set(this.normalizeGridLossTimestamp(p.timestamp as string), Number(p.value ?? 0));
        }
      }
      const seriesEntry: any = {
        name: this.getGridLossSeriesLabel(s.key),
        type: 'line',
        smooth: false,
        symbol: 'none',
        color: this.gridLossSeriesColors[s.key] ?? '#f97316',
        data: labels.map(ts => pointMap.get(ts) ?? null),
        connectNulls: false,
      };
      return seriesEntry;
    });

    const legendNames = series.map(s => this.getGridLossSeriesLabel(s.key));

    return {
      animationDurationUpdate: 250,
      animationEasingUpdate: 'cubicOut',
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: 'ctrl', moveOnMouseMove: false },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: 36,
          height: 24,
          borderColor: 'transparent',
          backgroundColor: 'rgba(0,0,0,0.04)',
          fillerColor: 'rgba(59,130,246,0.12)',
          handleStyle: { color: '#3b82f6', borderColor: '#3b82f6' },
          moveHandleStyle: { color: '#3b82f6' },
          selectedDataBackground: {
            lineStyle: { color: '#3b82f6' },
            areaStyle: { color: 'rgba(59,130,246,0.15)' },
          },
          dataBackground: {
            lineStyle: { color: '#d1d5db', width: 1 },
            areaStyle: { color: 'rgba(209,213,219,0.3)' },
          },
          textStyle: { color: '#9ca3af', fontSize: 10 },
          labelFormatter: (_value: number, valueStr: string) => this.formatGridLossTimestamp(valueStr),
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: (params: Array<{
          axisValue?: string | number;
          marker?: string;
          seriesName?: string;
          value?: string | number | null;
        }>) => this.formatGridLossTooltip(params),
        backgroundColor: 'rgba(0,0,0,0.75)',
        borderColor: 'transparent',
        textStyle: { color: '#fff', fontSize: 12 },
        padding: 10,
        extraCssText: 'border-radius:8px',
      },
      legend: {
        data: legendNames,
        bottom: 0,
        textStyle: { color: '#6b7280', fontSize: 11 },
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '22%',
        top: '4%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#9ca3af',
          fontSize: 10,
          interval: Math.max(0, Math.floor(labels.length / 10) - 1),
          formatter: (value: string) => this.formatGridLossTimestamp(value),
        },
      },
      yAxis: {
        type: 'value',
        min: yAxisRange?.min,
        max: yAxisRange?.max,
        name: 'kWh',
        nameTextStyle: { color: '#9ca3af', fontSize: 10, padding: [0, 0, 0, 0] },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      },
      series: [annotationSeries, ...echartSeries],
    };
  }

  private normalizeGridLossTimestamp(timestamp: string): string {
    const value = Date.parse(timestamp);
    return Number.isNaN(value) ? timestamp : new Date(value).toISOString();
  }

  private formatGridLossTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private formatGridLossTooltip(params: Array<{
    axisValue?: string | number;
    marker?: string;
    seriesName?: string;
    value?: string | number | null;
  }>): string {
    if (!params.length) {
      return '';
    }

    const rows = params.map(param => {
      const numericValue = Number(param.value);
      const value = param.value === null || param.value === undefined || !Number.isFinite(numericValue)
        ? '-'
        : numericValue.toLocaleString('da-DK', { maximumFractionDigits: 3 });
      return `${param.marker ?? ''}${param.seriesName ?? ''}: <strong>${value}</strong>`;
    });
    return [this.formatGridLossTimestamp(String(params[0].axisValue ?? '')), ...rows].join('<br/>');
  }

  private synchronizeGridLossYAxis(range?: EChartDataZoomRange): void {
    if (!this.synchronizeGridLossCharts) {
      return;
    }

    const ranges = [
      this.gridLossChart?.getVisibleValueRange(range),
      this.computedChart?.getVisibleValueRange(range),
    ].filter((valueRange): valueRange is EChartValueRange => valueRange !== undefined);

    if (!ranges.length) {
      return;
    }

    this.setGridLossYAxisRange({
      min: Math.min(0, ...ranges.map(valueRange => valueRange.min)),
      max: Math.max(0, ...ranges.map(valueRange => valueRange.max)),
    });
  }

  private setGridLossYAxisRange(range?: EChartValueRange): void {
    this.gridLossChart?.setYAxisRange(range);
    this.gridLossChartFullscreen?.setYAxisRange(range);
    this.computedChart?.setYAxisRange(range);
    this.computedChartFullscreen?.setYAxisRange(range);
  }

  private getGridLossSeriesValueRange(
    series: GridLossGraphSeries[],
    labels: string[]
  ): EChartValueRange | undefined {
    const timestamps = new Set(labels);
    const values = series
      .flatMap(item => item.points ?? [])
      .filter(point =>
        point.timestamp !== null &&
        point.timestamp !== undefined &&
        timestamps.has(this.normalizeGridLossTimestamp(point.timestamp as string))
      )
      .map(point => Number(point.value))
      .filter(value => Number.isFinite(value));

    return values.length
      ? {
          min: Math.min(0, ...values),
          max: Math.max(0, ...values),
        }
      : undefined;
  }

  private buildBarChartOptions(dates: string[], counts: number[]): any {
    const countLabel = this.translateService.instant('dashboard.graph.generated.yAxis');

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        backgroundColor: 'rgba(0,0,0,0.75)',
        borderColor: 'transparent',
        textStyle: { color: '#fff', fontSize: 12 },
        padding: 10,
        extraCssText: 'border-radius:8px',
        formatter: (params: any[]) => {
          const p = params[0];
          const label = countLabel === 'dashboard.graph.generated.yAxis' ? 'Antal' : countLabel;
          return `${p.name}<br/>${label}: <strong>${p.value}</strong>`;
        },
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '4%',
        top: '4%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: '#9ca3af',
          fontSize: 10,
          interval: Math.max(0, Math.floor(dates.length / 10) - 1),
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 28,
          itemStyle: { borderRadius: [4, 4, 0, 0], color: 'rgba(16,185,129,0.75)' },
          emphasis: { itemStyle: { color: 'rgba(16,185,129,1)' } },
          data: counts,
        },
      ],
    };
  }

  private buildEstimatedBarChartOptions(chartData: EstimatedTimeSeriesChartData): any {
    const countLabel = this.translateService.instant('dashboard.graph.estimated.count');
    const totalLabel = this.translateService.instant('dashboard.graph.estimated.total');

    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(0,0,0,0.75)',
        borderColor: 'transparent',
        textStyle: { color: '#fff', fontSize: 12 },
        padding: 10,
        extraCssText: 'border-radius:8px',
        formatter: (params: Array<{
          name?: string;
          marker?: string;
          seriesName?: string;
          value?: number;
          dataIndex?: number;
        }>) => {
          if (!params.length) {
            return '';
          }

          const rows = params
            .filter(param => Number(param.value ?? 0) > 0)
            .map(param =>
              `${param.marker ?? ''}${param.seriesName ?? ''}: ${countLabel} <strong>${param.value ?? 0}</strong>`,
            );
          const dataIndex = params[0].dataIndex ?? 0;
          return [
            params[0].name ?? '',
            ...rows,
            `${totalLabel}: <strong>${chartData.totals[dataIndex] ?? 0}</strong>`,
          ].join('<br/>');
        },
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '4%',
        top: '4%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: chartData.dates,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: '#9ca3af',
          fontSize: 10,
          interval: Math.max(0, Math.floor(chartData.dates.length / 10) - 1),
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
        splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } },
      },
      series: chartData.series.map(item => ({
        name: item.name,
        type: 'bar',
        stack: 'estimated-time-series',
        barMaxWidth: 28,
        itemStyle: { color: item.color },
        emphasis: { focus: 'series' },
        data: item.data,
      })),
    };
  }
}
